import React from "react";
import { Star, ExternalLink, PenLine } from "lucide-react";
import { Button } from "./ui/button";
import { useGoogleReviews } from "../hooks/useGoogleReviews";

export const GoogleG = ({ className = "w-5 h-5" }) => (
  <svg className={className} viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.6l6.8-6.8C35.8 2.5 30.3 0 24 0 14.6 0 6.5 5.4 2.6 13.2l7.9 6.1C12.4 13.6 17.7 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-2.8-.4-4H24v7.6h12.9c-.3 2.2-1.7 5.4-4.9 7.6l7.5 5.8c4.5-4.1 7-10.2 7-17z" />
    <path fill="#FBBC05" d="M10.5 28.7A14.6 14.6 0 0 1 9.7 24c0-1.6.3-3.2.8-4.7l-7.9-6.1A24 24 0 0 0 0 24c0 3.9.9 7.5 2.6 10.8l7.9-6.1z" />
    <path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.5-5.8c-2 1.4-4.7 2.4-8.4 2.4-6.3 0-11.6-4.1-13.5-9.9l-7.9 6.1C6.5 42.6 14.6 48 24 48z" />
  </svg>
);

export const Stars = ({ n, className = "w-4 h-4" }) => (
  <div className="flex items-center gap-0.5">
    {Array.from({ length: 5 }).map((_, k) => (
      <Star key={k} className={`${className} ${k < Math.round(n) ? "text-[#FBBC05] fill-[#FBBC05]" : "text-brand-200"}`} />
    ))}
  </div>
);

const ReviewCard = ({ r, fallbackUri }) => (
  <a
    href={r.google_maps_uri || fallbackUri}
    target="_blank"
    rel="noreferrer"
    data-testid="google-review-card"
    className="group flex flex-col rounded-3xl p-7 bg-white border border-brand-100 hover:border-brand-300 hover:-translate-y-1 hover:shadow-xl transition-all"
  >
    <div className="flex items-center gap-3">
      {r.author_photo_uri ? (
        <img src={r.author_photo_uri} alt="" referrerPolicy="no-referrer" className="w-11 h-11 rounded-full object-cover" />
      ) : (
        <div className="w-11 h-11 rounded-full bg-brand-100 flex items-center justify-center font-semibold text-brand-800">
          {(r.author_name || "?")[0]}
        </div>
      )}
      <div className="min-w-0">
        <div className="font-semibold text-brand-900 truncate">{r.author_name}</div>
        <div className="text-[12px] text-brand-500">{r.relative_time}</div>
      </div>
      <GoogleG className="w-5 h-5 ml-auto shrink-0" />
    </div>
    <Stars n={r.rating} className="w-4 h-4 mt-4" />
    <p className="mt-3 text-[15px] leading-relaxed text-brand-700 whitespace-pre-line line-clamp-6">{r.text}</p>
    <span className="mt-auto pt-5 text-[12px] text-brand-500 inline-flex items-center gap-1 group-hover:text-brand-800">
      Auf Google ansehen <ExternalLink className="w-3 h-3" />
    </span>
  </a>
);

const Testimonials = () => {
  const { data, failed } = useGoogleReviews();

  if (failed) return null;

  return (
    <section id="reviews" className="py-24 bg-brand-50/50" data-testid="google-reviews-section">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <div className="text-[12px] tracking-[0.3em] uppercase text-accent2 font-semibold">
              Bewertungen auf Google
            </div>
            <h2 className="mt-3 font-display text-[36px] md:text-[46px] leading-tight font-extrabold text-brand-900">
              Vertrauen, das wir uns täglich verdienen.
            </h2>
          </div>

          <div className="lg:col-span-5 flex justify-start lg:justify-end">
            {data ? (
              <div className="inline-flex items-center gap-5 bg-white rounded-2xl border border-brand-100 px-6 py-4 shadow-sm" data-testid="google-rating-summary">
                <GoogleG className="w-9 h-9" />
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-display text-3xl font-extrabold text-brand-900">
                      {data.rating?.toFixed(1).replace(".", ",")}
                    </span>
                    <Stars n={data.rating} className="w-5 h-5" />
                  </div>
                  <div className="text-[13px] text-brand-600">
                    {data.user_rating_count} Google-Bewertungen
                  </div>
                </div>
              </div>
            ) : (
              <div className="h-[76px] w-72 rounded-2xl bg-white/70 animate-pulse" />
            )}
          </div>
        </div>

        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {data
            ? data.reviews.map((r) => (
                <ReviewCard key={r.author_name} r={r} fallbackUri={data.google_maps_uri} />
              ))
            : Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="h-64 rounded-3xl bg-white/70 animate-pulse" />
              ))}
        </div>

        {data && (
          <div className="mt-12 flex flex-wrap items-center justify-center gap-3">
            <Button asChild className="bg-brand-900 hover:bg-brand-800 text-white rounded-full h-12 px-6">
              <a href={data.write_review_uri} target="_blank" rel="noreferrer" data-testid="write-google-review-btn" className="inline-flex items-center gap-2">
                <PenLine className="w-4 h-4" /> Jetzt auf Google bewerten
              </a>
            </Button>
            <Button asChild variant="outline" className="rounded-full h-12 px-6 border-brand-200 text-brand-900 hover:bg-white">
              <a href={data.google_maps_uri} target="_blank" rel="noreferrer" data-testid="all-google-reviews-btn" className="inline-flex items-center gap-2">
                Alle Bewertungen auf Google <ExternalLink className="w-4 h-4" />
              </a>
            </Button>
          </div>
        )}
      </div>
    </section>
  );
};

export default Testimonials;
